import { AuthClientInstance, getAuthClient } from "@trumpf/ux.utils";
import { authClientName, EnvConfig } from "../config";

export interface RequestOptions<T> {
    method?: string;
    headers?: HeadersInit;
    body?: unknown;
    mockResponse?: T;
    binary?: boolean;
    responseType?: "json" | "text" | "blob";
}

export interface ApiClientInterface {
    request: <T>(path: string, options?: RequestOptions<T>) => Promise<T>;
}

export class ApiClient implements ApiClientInterface {
    protected config: EnvConfig;
    protected token: string | null = null;
    protected authClientServiceCalls: AuthClientInstance = getAuthClient(authClientName);

    constructor(config: EnvConfig) {
        this.config = config;
        this.token = this.authClientServiceCalls.getAccessToken();
        if (this.token) {
            this.onAuth();
        } else {
            this.authClientServiceCalls.subscribe((token) => {
                this.token = token || null;
                this.onAuth();
            });
        }
    }

    protected onAuth() {
        // placeholder for any action to be taken on authentication
    }

    async request<T>(path: string, options?: RequestOptions<T>): Promise<T> {
        if (this.config.useMockData) {
            return this.mockRequest<T>(options?.mockResponse as T);
        }

        if (this.authClientServiceCalls === null) {
            return Promise.reject("AuthClient not initialized");
        }
        if (!this.token) {
            return Promise.reject("No token");
        }

        const { method = "POST", headers = {}, body, binary } = options || {};

        const response = await fetch(`${path}`, {
            method,
            headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
                Authorization: `Bearer ${this.token}`,
                ...headers
            },
            body: binary
                ? (body as BodyInit)
                : JSON.stringify({
                      ...(body as object)
                  })
        });

        if (response.status === 200) {
            if (options?.responseType === "blob") {
                return response.blob() as Promise<T>;
            }
            return response.json();
        }
        return Promise.reject(`Error ${response.status}`);
    }

    protected async mockRequest<T>(mockResponse: T): Promise<T> {
        return new Promise((resolve) => {
            setTimeout(() => {
                resolve(mockResponse);
            }, 1000);
        });
    }
}
